import type { AppRole } from '@empower-safety/shared-types';
import { CheckCircle2, Copy, Eye, EyeOff, Loader2, Plug, PlugZap, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  type ClinicalMatcherDto,
  type HrisIntegrationDto,
  type HrisProvider,
  useDeleteHris,
  useHrisIntegrations,
  useHrisProviders,
  useUpsertHris,
} from '../api/useHris.js';
import { useTeamsList } from '../api/useTeams.js';
import { cn } from '../lib/cn.js';

// Settings → HR Integrations.
//
// Shows one card per configured integration with its webhook URL (copyable), clinical
// matcher rules, default role/teams, and active/auto-invite toggles. Provider catalog
// is read from the server so adding a new adapter backend-side surfaces it here
// without a dashboard redeploy.

export function HrisIntegrationsSection({ canEdit }: { canEdit: boolean }) {
  const providers = useHrisProviders();
  const integrations = useHrisIntegrations();
  const [editing, setEditing] = useState<{
    mode: 'new' | 'edit';
    provider?: HrisProvider;
    existing?: HrisIntegrationDto;
  } | null>(null);

  const configured = new Set((integrations.data?.integrations ?? []).map((i) => i.provider));
  const available = (providers.data?.providers ?? []).filter((p) => !configured.has(p.provider));

  return (
    <div className="rounded-lg bg-white p-6 shadow-panel">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-semibold">HR integrations</h2>
          <p className="mt-0.5 text-xs text-ink-faint">
            Auto-invite new clinical hires when they sign a contract in your HRIS. Each integration
            gets its own webhook URL to paste into the provider.
          </p>
        </div>
      </div>

      {(providers.isError || integrations.isError) && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          Failed to load HRIS providers:{' '}
          {(providers.error as Error | undefined)?.message ?? 'unknown error'}
          {integrations.error ? ` · ${(integrations.error as Error).message}` : ''}
        </div>
      )}

      {providers.isLoading && (
        <div className="py-6 text-center text-xs text-ink-faint">Loading providers…</div>
      )}

      <div className="space-y-3">
        {(integrations.data?.integrations ?? []).map((it) => (
          <IntegrationCard
            key={it.id}
            integration={it}
            canEdit={canEdit}
            onEdit={() => setEditing({ mode: 'edit', provider: it.provider, existing: it })}
          />
        ))}
      </div>

      {canEdit && available.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
          {available.map((p) => (
            <button
              key={p.provider}
              type="button"
              onClick={() => setEditing({ mode: 'new', provider: p.provider })}
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line-strong bg-surface-3 px-3 py-1.5 text-xs font-medium text-ink-dim hover:border-brand-purple hover:text-brand-purple"
            >
              <Plug className="h-3.5 w-3.5" />
              Connect {p.display_name}
            </button>
          ))}
        </div>
      )}

      {!providers.isLoading &&
        !providers.isError &&
        (integrations.data?.integrations ?? []).length === 0 &&
        available.length === 0 && (
          <div className="py-6 text-center text-xs text-ink-faint">
            No HRIS providers available on this install.
          </div>
        )}

      {editing && (
        <IntegrationEditor
          mode={editing.mode}
          provider={editing.provider}
          existing={editing.existing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function IntegrationCard({
  integration,
  canEdit,
  onEdit,
}: {
  integration: HrisIntegrationDto;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const del = useDeleteHris();
  return (
    <div className="rounded-md border border-line bg-surface-3 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PlugZap
            className={cn('h-4 w-4', integration.active ? 'text-emerald-600' : 'text-ink-mute')}
          />
          <div>
            <div className="text-sm font-semibold text-ink capitalize">
              {integration.display_name ?? integration.provider.replace('_', ' ')}
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ink-faint">
              <span>{integration.active ? 'Active' : 'Disabled'}</span>
              <span>·</span>
              <span>{integration.auto_invite ? 'Auto-invite on' : 'Dry run (no invites)'}</span>
              {!integration.has_signing_secret && (
                <>
                  <span>·</span>
                  <span className="text-amber-700">No signing secret</span>
                </>
              )}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="rounded px-2 py-0.5 text-xs font-medium text-brand-purple hover:bg-brand-purple/10"
            >
              Configure
            </button>
            <button
              type="button"
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(`Disconnect ${integration.provider}?`))
                  del.mutate(integration.id);
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              <Trash2 className="h-3 w-3" />
              Disconnect
            </button>
          </div>
        )}
      </div>
      <CopyField label="Webhook URL" value={integration.webhook_url} />
      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-ink-faint">
        {integration.clinical_matcher?.positions?.map((p) => (
          <span
            key={`pos-${p}`}
            className="rounded-full bg-white px-2 py-0.5 ring-1 ring-inset ring-gray-200"
          >
            position: {p}
          </span>
        ))}
        {integration.clinical_matcher?.departments?.map((d) => (
          <span
            key={`dept-${d}`}
            className="rounded-full bg-white px-2 py-0.5 ring-1 ring-inset ring-gray-200"
          >
            dept: {d}
          </span>
        ))}
        {integration.invite_all && (
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
            Invite everyone
          </span>
        )}
        <span className="rounded-full bg-white px-2 py-0.5 ring-1 ring-inset ring-gray-200 capitalize">
          default role: {integration.default_app_role}
        </span>
      </div>
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function doCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* no-op */
    }
  }
  return (
    <div>
      <div className="mb-0.5 text-[11px] font-medium uppercase tracking-wider text-ink-faint">
        {label}
      </div>
      <div className="flex items-center gap-1">
        <code className="flex-1 overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] ring-1 ring-inset ring-gray-200">
          {value}
        </code>
        <button
          type="button"
          onClick={doCopy}
          className="inline-flex items-center gap-1 rounded border border-line bg-white px-2 py-1 text-[11px] hover:bg-surface-3"
        >
          {copied ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

const APP_ROLES: AppRole[] = ['worker', 'monitor', 'admin'];

function IntegrationEditor({
  mode,
  provider,
  existing,
  onClose,
}: {
  mode: 'new' | 'edit';
  provider?: HrisProvider;
  existing?: HrisIntegrationDto;
  onClose: () => void;
}) {
  const teams = useTeamsList();
  const upsert = useUpsertHris();
  const [positions, setPositions] = useState(
    existing?.clinical_matcher?.positions?.join(', ') ??
      'Nurse, Physiotherapist, Occupational Therapist, Speech Pathologist, Social Worker',
  );
  const [departments, setDepartments] = useState(
    existing?.clinical_matcher?.departments?.join(', ') ?? 'Clinical, Allied Health',
  );
  const [defaultRole, setDefaultRole] = useState<AppRole>(existing?.default_app_role ?? 'worker');
  const [defaultTeamIds, setDefaultTeamIds] = useState<string[]>(existing?.default_team_ids ?? []);
  const [inviteAll, setInviteAll] = useState(Boolean(existing?.invite_all));
  const [autoInvite, setAutoInvite] = useState(existing?.auto_invite !== false);
  const [active, setActive] = useState(existing?.active ?? true);
  const [signingSecret, setSigningSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savedIntegration, setSavedIntegration] = useState<HrisIntegrationDto | null>(null);

  const effectiveProvider = provider ?? existing?.provider;
  const allTeams = teams.data?.teams ?? [];

  function toggleTeam(id: string) {
    setDefaultTeamIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function parseList(text: string): string[] | undefined {
    const parts = text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length > 0 ? parts : undefined;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!effectiveProvider) return;
    const clinical: ClinicalMatcherDto | null = inviteAll
      ? null
      : {
          positions: parseList(positions),
          departments: parseList(departments),
        };
    const payload = {
      provider: effectiveProvider,
      ...(signingSecret.trim() ? { signing_secret: signingSecret.trim() } : {}),
      clinical_matcher: clinical,
      default_app_role: defaultRole,
      default_team_ids: defaultTeamIds,
      invite_all: inviteAll,
      auto_invite: autoInvite,
      active,
    };
    const res = await upsert.mutateAsync(payload);
    setSavedIntegration(res.integration);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hris-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-2xl"
      >
        <h2 id="hris-title" className="mb-1 text-base font-semibold text-ink">
          {mode === 'new' ? 'Connect' : 'Configure'} {effectiveProvider?.replace('_', ' ')}
        </h2>
        <p className="mb-4 text-xs text-ink-faint">
          When {effectiveProvider?.replace('_', ' ')} fires a contract-signed or hired event, we'll
          invite the new hire automatically (matching your clinical rules).
        </p>

        {savedIntegration ? (
          <SavedView integration={savedIntegration} onClose={onClose} />
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">
                Signing secret{' '}
                {existing?.has_signing_secret && (
                  <span className="text-[10px] text-ink-faint">(leave blank to keep existing)</span>
                )}
              </span>
              <div className="flex items-center gap-1">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={signingSecret}
                  onChange={(e) => setSigningSecret(e.target.value)}
                  placeholder={
                    existing?.has_signing_secret
                      ? '••••••••'
                      : 'Paste or leave blank to auto-generate'
                  }
                  className="flex-1 rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="rounded border border-line bg-white px-2 py-1 text-xs text-ink-faint hover:bg-surface-3"
                >
                  {showSecret ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </button>
              </div>
              <span className="text-[10px] text-ink-faint">
                Paste this value into {effectiveProvider?.replace('_', ' ')}'s webhook
                configuration.
              </span>
            </label>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="inv-all"
                  checked={inviteAll}
                  onChange={(e) => setInviteAll(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
                />
                <label htmlFor="inv-all" className="text-xs font-medium text-ink-dim">
                  Invite everyone (skip clinical matcher)
                </label>
              </div>

              {!inviteAll && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-dim">Clinical positions</span>
                    <textarea
                      value={positions}
                      onChange={(e) => setPositions(e.target.value)}
                      placeholder="Nurse, Physio, OT"
                      rows={2}
                      className="rounded-md border border-line-strong px-3 py-1.5 text-xs focus:border-brand-purple focus:outline-none"
                    />
                    <span className="text-[10px] text-ink-faint">
                      Comma-separated, case-insensitive contains.
                    </span>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-ink-dim">Clinical departments</span>
                    <textarea
                      value={departments}
                      onChange={(e) => setDepartments(e.target.value)}
                      placeholder="Clinical, Allied Health"
                      rows={2}
                      className="rounded-md border border-line-strong px-3 py-1.5 text-xs focus:border-brand-purple focus:outline-none"
                    />
                    <span className="text-[10px] text-ink-faint">
                      Match on either positions or departments.
                    </span>
                  </label>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-1 text-xs font-medium text-ink-dim">Default app role</div>
                <div className="flex flex-wrap gap-1">
                  {APP_ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setDefaultRole(r)}
                      className={cn(
                        'rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize transition',
                        defaultRole === r
                          ? 'border-brand-purple bg-brand-purple text-white'
                          : 'border-line bg-white text-ink-dim hover:border-line-strong',
                      )}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-ink-dim">Default teams</div>
                {allTeams.length === 0 ? (
                  <span className="text-xs text-ink-mute">No teams yet.</span>
                ) : (
                  <div className="flex max-h-16 flex-wrap gap-1 overflow-y-auto">
                    {allTeams.map((t) => {
                      const active = defaultTeamIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => toggleTeam(t.id)}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                            active
                              ? 'border-brand-purple bg-brand-purple text-white'
                              : 'border-line bg-white text-ink-dim hover:border-line-strong',
                          )}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 border-t border-line pt-3 text-xs">
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
                />
                Active
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={autoInvite}
                  onChange={(e) => setAutoInvite(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple"
                />
                Auto-invite
              </label>
              <span className="text-[10px] text-ink-faint">
                Turn off auto-invite for a dry-run — we'll verify + log the webhook but not invite
                anyone.
              </span>
            </div>

            {upsert.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {(upsert.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-line pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={upsert.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
              >
                {upsert.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plug className="h-3.5 w-3.5" />
                )}
                {mode === 'new' ? 'Connect' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SavedView({
  integration,
  onClose,
}: {
  integration: HrisIntegrationDto;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
        <div className="font-semibold">Integration saved</div>
        <div className="mt-0.5 text-xs">
          Copy the webhook URL + signing secret into {integration.provider.replace('_', ' ')}'s
          admin.
        </div>
      </div>
      <CopyField label="Webhook URL" value={integration.webhook_url} />
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        For your security, the signing secret is only shown once — when you submit the form. Scroll
        up in this session to copy it, or generate a new one by saving again with a blank secret
        field.
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple"
        >
          Done
        </button>
      </div>
    </div>
  );
}
